import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  ExternalLink,
  FileUp,
  Receipt,
  Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';

const todayIso = () => new Date().toISOString().split('T')[0];
const money = (value) => `CHF ${(Number(value) || 0).toFixed(2)}`;

function invoiceStatus(order) {
  const today = todayIso();
  if (order.phase === 5 || order.archived) {
    return { key: 'paid', label: 'Bezahlt', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle };
  }
  if (order.invoice_sent_at && order.invoice_due_date && order.invoice_due_date < today) {
    return { key: 'due', label: 'Fällig', color: 'bg-red-100 text-red-700', icon: AlertTriangle };
  }
  if (order.invoice_sent_at) {
    return { key: 'unpaid', label: 'Unbezahlt', color: 'bg-amber-100 text-amber-700', icon: Clock };
  }
  return { key: 'draft', label: 'Nicht gesendet', color: 'bg-muted text-muted-foreground', icon: Clock };
}

function inPeriod(order, period) {
  if (period === 'all') return true;
  const raw = order.invoice_sent_at || order.created_at || order.phase_started_at;
  if (!raw) return false;
  const date = new Date(raw);
  const now = new Date();
  if (period === 'this-year') return date.getFullYear() === now.getFullYear();
  if (period === 'last-year') return date.getFullYear() === now.getFullYear() - 1;
  if (period === 'this-month') {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }
  return true;
}

function monthlyTotals(orders) {
  const values = Array.from({ length: 12 }, () => 0);
  orders.forEach(order => {
    const raw = order.invoice_sent_at || order.created_at || order.phase_started_at;
    if (!raw) return;
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      values[date.getMonth()] += Number(order.totals?.total || 0);
    }
  });
  return values;
}

export default function InvoicesPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [period, setPeriod] = useState('this-year');
  const [search, setSearch] = useState('');
  const [bankImporting, setBankImporting] = useState(false);
  const [bankResult, setBankResult] = useState(null);
  const bankInputRef = useRef(null);

  const loadInvoices = () => {
    return api.get('/orders/invoices')
      .then(res => setOrders(res.data))
      .catch(() => toast.error('Rechnungen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const handleBankImport = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setBankImporting(true);
    try {
      const res = await api.post('/bank/reconcile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBankResult(res.data);
      toast.success(`${res.data.paid || 0} Rechnung(en) bezahlt gesetzt`);
      await loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bankdatei konnte nicht abgeglichen werden');
    } finally {
      if (bankInputRef.current) bankInputRef.current.value = '';
      setBankImporting(false);
    }
  };

  const customers = useMemo(() => {
    return Array.from(new Set(orders.map(o => (o.customer_name || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'de-CH'));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter(order => {
      const status = invoiceStatus(order).key;
      if (statusFilter === 'paid' && status !== 'paid') return false;
      if (statusFilter === 'unpaid' && status === 'paid') return false;
      if (statusFilter === 'due' && status !== 'due') return false;
      if (customerFilter !== 'all' && (order.customer_name || '').trim() !== customerFilter) return false;
      if (!inPeriod(order, period)) return false;
      if (!q) return true;
      return [
        order.order_number,
        order.title,
        order.customer_name,
        order.property_address,
        order.object_name,
        order.renter_name,
        order.monteur_company,
        order.invoice_due_date,
      ].some(v => (v || '').toString().toLowerCase().includes(q));
    });
  }, [orders, statusFilter, customerFilter, period, search]);

  const total = filteredOrders.reduce((sum, o) => sum + Number(o.totals?.total || 0), 0);
  const unpaidTotal = filteredOrders
    .filter(o => invoiceStatus(o).key !== 'paid')
    .reduce((sum, o) => sum + Number(o.totals?.total || 0), 0);
  const paidTotal = filteredOrders
    .filter(o => invoiceStatus(o).key === 'paid')
    .reduce((sum, o) => sum + Number(o.totals?.total || 0), 0);
  const overdueCount = filteredOrders.filter(o => invoiceStatus(o).key === 'due').length;
  const monthly = monthlyTotals(filteredOrders);
  const maxMonth = Math.max(...monthly, 1);
  const monthLabels = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];

  return (
    <Layout>
      <div className="p-6 md:p-8 pb-20" data-testid="invoices-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Receipt className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="font-heading font-black text-3xl">Rechnungen</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {filteredOrders.length} Rechnungen · Zusammen {money(total)}
              </p>
            </div>
          </div>
          <div>
            <input
              ref={bankInputRef}
              type="file"
              accept=".csv,.xml,.camt,text/csv,application/xml,text/xml"
              className="hidden"
              onChange={e => handleBankImport(e.target.files?.[0])}
            />
            <Button variant="outline" onClick={() => bankInputRef.current?.click()} disabled={bankImporting} className="rounded-sm gap-2">
              <FileUp className="w-4 h-4" strokeWidth={1.5} />
              {bankImporting ? 'Abgleichen...' : 'Bankdatei importieren'}
            </Button>
          </div>
        </div>

        {bankResult && (
          <div className="bg-card border border-border/60 rounded-sm p-4 mb-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-sm">Bankabgleich abgeschlossen</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {bankResult.transactions} Buchungen gelesen Â· {bankResult.matched?.length || 0} Treffer Â· {bankResult.paid || 0} neu bezahlt gesetzt
                </p>
              </div>
              <button onClick={() => setBankResult(null)} className="text-xs text-muted-foreground hover:text-foreground">Schliessen</button>
            </div>
            {bankResult.matched?.length > 0 && (
              <div className="mt-3 space-y-1">
                {bankResult.matched.slice(0, 5).map(match => (
                  <div key={`${match.order_id}-${match.reference}`} className="flex items-center justify-between text-xs bg-muted/60 rounded-sm px-3 py-2">
                    <span>{match.order_number} Â· {match.title || 'Rechnung'}</span>
                    <span className="font-mono">{match.amount ? money(match.amount) : 'Referenz erkannt'}</span>
                  </div>
                ))}
              </div>
            )}
            {bankResult.skipped?.length > 0 && (
              <p className="text-xs text-amber-700 mt-2">{bankResult.skipped.length} Treffer wurden nicht automatisch gebucht.</p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 mb-5">
          <div className="flex flex-wrap gap-2">
            {[
              ['all', 'Alle'],
              ['paid', 'Bezahlt'],
              ['unpaid', 'Unbezahlt'],
              ['due', 'Fällig'],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={cn(
                  'h-10 px-5 rounded-sm border text-sm font-medium transition-colors',
                  statusFilter === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border/70 hover:bg-accent'
                )}
              >
                {label}
              </button>
            ))}
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="rounded-sm w-48"><SelectValue placeholder="Alle Verwaltungen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Verwaltungen</SelectItem>
                {customers.map(customer => <SelectItem key={customer} value={customer}>{customer}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="rounded-sm w-40"><SelectValue placeholder="Zeitraum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="this-year">Dieses Jahr</SelectItem>
                <SelectItem value="this-month">Dieser Monat</SelectItem>
                <SelectItem value="last-year">Letztes Jahr</SelectItem>
                <SelectItem value="all">Alle Jahre</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen" className="pl-9 rounded-sm" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border/60 rounded-sm p-5">
            <p className="text-sm text-muted-foreground">Rechnungen ({filteredOrders.length})</p>
            <p className="font-heading text-3xl mt-2 text-primary">{money(total)}</p>
          </div>
          <div className="bg-card border border-border/60 rounded-sm p-5">
            <p className="text-sm text-muted-foreground">Unbezahlt</p>
            <p className="font-heading text-3xl mt-2 text-[#5b243a]">{money(unpaidTotal)}</p>
          </div>
          <div className="bg-card border border-border/60 rounded-sm p-5">
            <p className="text-sm text-muted-foreground">Bezahlt</p>
            <p className="font-heading text-3xl mt-2 text-emerald-600">{money(paidTotal)}</p>
            {overdueCount > 0 && <p className="text-xs text-red-600 mt-2">{overdueCount} fällige Rechnungen</p>}
          </div>
        </div>

        <div className="bg-card border border-border/60 rounded-sm p-5 mb-6">
          <div className="h-64 flex items-end gap-3 border-b border-border/70 px-4">
            {monthly.map((value, idx) => (
              <div key={monthLabels[idx]} className="flex-1 h-full flex flex-col justify-end items-center gap-2">
                <div
                  className="w-full max-w-8 bg-primary rounded-t-sm transition-all"
                  style={{ height: `${Math.max(4, (value / maxMonth) * 86)}%` }}
                  title={money(value)}
                />
                <span className="text-xs text-muted-foreground">{monthLabels[idx]}</span>
              </div>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-16">
            <Receipt className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1} />
            <p className="text-muted-foreground">Keine Rechnungen gefunden</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredOrders.map(o => {
              const status = invoiceStatus(o);
              const StatusIcon = status.icon;
              return (
                <div key={o.id} className="flex items-center justify-between gap-4 p-4 bg-card border border-border/60 rounded-sm hover:border-primary/30 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-muted-foreground">{o.order_number}</span>
                      <span className={cn('flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-sm font-medium', status.color)}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </div>
                    <p className="font-medium text-sm text-foreground truncate">{o.title}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                      {o.customer_name && <span className="text-xs text-muted-foreground">{o.customer_name}</span>}
                      {o.invoice_sent_at && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" strokeWidth={1.5} />
                          Gesendet {new Date(o.invoice_sent_at).toLocaleDateString('de-CH')}
                        </span>
                      )}
                      {o.invoice_due_date && (
                        <span className={cn('flex items-center gap-1 text-xs', status.key === 'due' ? 'text-red-600 font-medium' : 'text-muted-foreground')}>
                          <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                          Fällig {new Date(o.invoice_due_date + 'T00:00:00').toLocaleDateString('de-CH')}
                        </span>
                      )}
                      {o.last_reminder_sent && (
                        <span className="text-xs text-amber-700">
                          Mahnung {new Date(o.last_reminder_sent).toLocaleDateString('de-CH')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-sm font-mono font-semibold">{money(o.totals?.total)}</span>
                    <Link to={`/orders/${o.id}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Öffnen
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="fixed bottom-0 left-0 lg:left-56 right-0 border-t border-border/70 bg-muted/95 backdrop-blur px-6 py-3">
          <div className="max-w-full flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-sm">
            <span><b>{filteredOrders.length}</b> RECHNUNGEN</span>
            <span>ZUSAMMEN: <b>{money(total)}</b></span>
            <span>UNBEZAHLT: <b>{money(unpaidTotal)}</b></span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
